import { useEffect, useRef, useState, ReactNode } from "react"
import { Close } from "@mui/icons-material"
import { Box, IconButton, InputAdornment, TextField } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSearch } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { useDebouncedCallback } from "use-debounce"

import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import type { TranslationKey } from "../../../../modules/localization/translations"

type StyleProps = Readonly<{
    isActiveInput: boolean
    activeWidth: string
    inactiveWidth: string
}>

const useStyles = makeStyles<StyleProps>()((theme, { isActiveInput, activeWidth, inactiveWidth }) => ({
    searchWrapper: {
        display: "flex",
        alignItems: "center",
        transition: "width 0.3s ease",
        overflow: "hidden",
        width: isActiveInput ? activeWidth : inactiveWidth,
    },
    search: {
        "& .MuiOutlinedInput-root": {
            padding: "0 .5rem",
            borderRadius: ".75rem",
            cursor: "pointer",
            boxShadow: "0px 2px 1px -1px rgba(0, 0, 0, 0.12)",
            height: "2.5rem",
        },
        "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
            transition: "all 0.3s ease-out",
            borderWidth: "1px",
        },
        "& .MuiSvgIcon-root": {
            color: theme.palette.tailwind.slate[800],
        },
        "& .MuiInputBase-input": {
            height: "1.2rem",
        },
    },
    searchCollapsed: {
        "& .MuiOutlinedInput-root": {
            justifyContent: "center",
        },
        "& .MuiInputAdornment-positionStart": {
            marginRight: 0,
        },
        "& .MuiInputBase-input": {
            width: 0,
            minWidth: 0,
            padding: 0,
        },
    },
    searchIcon: {
        color: theme.palette.tailwind.slate[800],
        fontSize: "1.125rem",
    },
    // keeps the clear button off the trailing action when both are present
    endAdornment: {
        gap: ".25rem",
    },
    disabledContent: {
        pointerEvents: "none",

        "& .MuiSvgIcon-root": {
            color: "rgba(0, 0, 0, 0.26)",
        },
    },
}))

type FilterSearchProps = Readonly<{
    search: string
    setSearch: (searchText: string) => void
    disabled?: boolean
    activeWidth?: string
    inactiveWidth?: string
    debounceMs?: number
    defaultActive?: boolean
    placeholderId?: TranslationKey
    className?: string
    // rendered inside the field, after the clear button
    trailingAction?: ReactNode
}>

export function FilterSearch({
    search,
    setSearch,
    disabled = false,
    activeWidth = "9rem",
    inactiveWidth = "2.5rem",
    debounceMs = 300,
    defaultActive = false,
    placeholderId = "create-order-add-search-input",
    className,
    trailingAction,
}: FilterSearchProps) {
    const [inputValue, setInputValue] = useState(search)
    const [isActiveInput, setIsActiveInput] = useState(defaultActive)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const { classes, cx } = useStyles({ isActiveInput, activeWidth, inactiveWidth })

    const debouncedPublishSearch = useDebouncedCallback((value: string) => {
        setSearch(value)
    }, debounceMs)

    useEffect(() => {
        debouncedPublishSearch(inputValue)
    }, [inputValue, debouncedPublishSearch])

    useEffect(() => {
        return () => {
            debouncedPublishSearch.cancel()
        }
    }, [debouncedPublishSearch])

    useEffect(() => {
        setInputValue((prev) => (search === prev ? prev : search))
    }, [search])

    const handleClear = () => {
        debouncedPublishSearch.cancel()
        setInputValue("")
        setSearch("")
        if (!defaultActive) {
            setIsActiveInput(false)
            inputRef.current?.blur()
        }
    }

    const hasValue = inputValue.trim().length > 0

    return (
        <Box className={cx(classes.searchWrapper, className, { [classes.disabledContent]: disabled })}>
            <LocalizedStrict id={placeholderId} attrs={{ placeholder: true }}>
                <TextField
                    name="search"
                    value={inputValue}
                    size="small"
                    fullWidth
                    placeholder="Search"
                    inputRef={inputRef}
                    onFocus={() => setIsActiveInput(true)}
                    onBlur={() => {
                        debouncedPublishSearch.flush()
                        if (!defaultActive && !inputValue.trim()) setIsActiveInput(false)
                    }}
                    onChange={(e) => setInputValue(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position="start"
                                onClick={() => {
                                    inputRef.current?.focus()
                                    setIsActiveInput(true)
                                }}
                            >
                                <FontAwesomeIcon icon={faSearch} className={classes.searchIcon} />
                            </InputAdornment>
                        ),
                        endAdornment: (hasValue || trailingAction) && (
                            <InputAdornment position="end" className={classes.endAdornment}>
                                {hasValue && (
                                    <IconButton
                                        onClick={handleClear}
                                        size="small"
                                        edge={trailingAction ? false : "end"}
                                    >
                                        <Close fontSize="small" />
                                    </IconButton>
                                )}
                                {trailingAction}
                            </InputAdornment>
                        ),
                    }}
                    disabled={disabled}
                    className={cx(classes.search, !isActiveInput && classes.searchCollapsed)}
                />
            </LocalizedStrict>
        </Box>
    )
}
